/**
 * AutomatedCampaigns - Birthday Rewards, Re-engagement, and A/B Testing (Refactored)
 */
import { useState, useEffect, useCallback } from 'react';
import { Cake, UserMinus, FlaskConical, Calendar, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

// Import sub-components
import {
  AutomatedStatsOverview,
  BirthdayRewardsTab,
  ReEngagementTab,
  ABTestingTab,
  ActivityLogTab
} from './automated';

export default function AutomatedCampaigns() {
  // View state
  const [activeTab, setActiveTab] = useState('birthday');
  const [loading, setLoading] = useState(true);
  
  // Stats
  const [stats, setStats] = useState(null);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  
  // Birthday
  const [birthdaySettings, setBirthdaySettings] = useState(null);
  const [upcomingBirthdays, setUpcomingBirthdays] = useState([]);
  const [sendingBirthday, setSendingBirthday] = useState(null);
  
  // Re-engagement
  const [reengagementSettings, setReengagementSettings] = useState(null);
  const [eligibleContacts, setEligibleContacts] = useState([]);
  const [sendingReengagement, setSendingReengagement] = useState(null);
  
  // A/B Tests
  const [abTests, setAbTests] = useState([]);
  const [showCreateABTest, setShowCreateABTest] = useState(false);
  const [newABTest, setNewABTest] = useState({ campaign_id: 1, variant_name: '', discount_percent: 15, description: '' });
  const [abTestResults, setAbTestResults] = useState(null);
  
  // Campaign Log
  const [campaignLog, setCampaignLog] = useState([]);

  // === DATA FETCHING ===

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, birthdayRes, upcomingRes, reengageRes, eligibleRes, abRes, logRes, schedulerRes] = await Promise.all([
        api.get('/automated-campaigns/stats'),
        api.get('/automated-campaigns/birthday/settings'),
        api.get('/automated-campaigns/birthday/upcoming', { params: { days: 14 } }),
        api.get('/automated-campaigns/reengagement/settings'),
        api.get('/automated-campaigns/reengagement/eligible'),
        api.get('/automated-campaigns/ab-tests'),
        api.get('/automated-campaigns/log', { params: { limit: 20 } }),
        api.get('/automated-campaigns/scheduler')
      ]);
      
      setStats(statsRes.data);
      setBirthdaySettings(birthdayRes.data);
      setUpcomingBirthdays(upcomingRes.data.upcoming_birthdays || []);
      setReengagementSettings(reengageRes.data);
      setEligibleContacts(eligibleRes.data.eligible_contacts || []);
      setAbTests(abRes.data.ab_tests || []);
      setCampaignLog(logRes.data.logs || []);
      setSchedulerStatus(schedulerRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // === ACTION HANDLERS ===

  const updateBirthdaySettings = async (updates) => {
    const newSettings = { ...birthdaySettings, ...updates };
    setBirthdaySettings(newSettings);
    
    try {
      await api.put('/automated-campaigns/birthday/settings', newSettings);
      toast.success('Birthday settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const updateReengagementSettings = async (updates) => {
    const newSettings = { ...reengagementSettings, ...updates };
    setReengagementSettings(newSettings);
    
    try {
      await api.put('/automated-campaigns/reengagement/settings', newSettings);
      toast.success('Re-engagement settings updated');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const sendBirthdayReward = async (contactId) => {
    setSendingBirthday(contactId);
    try {
      const res = await api.post(`/automated-campaigns/birthday/send?contact_id=${contactId}`);
      toast.success(`Birthday reward sent! Code: ${res.data.code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send birthday reward');
    }
    setSendingBirthday(null);
  };

  const sendReengagementOffer = async (contactId) => {
    setSendingReengagement(contactId);
    try {
      const res = await api.post(`/automated-campaigns/reengagement/send?contact_id=${contactId}`);
      toast.success(`Re-engagement offer sent! Code: ${res.data.code}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send offer');
    }
    setSendingReengagement(null);
  };

  const createABTest = async () => {
    if (!newABTest.variant_name) {
      toast.error('Variant name required');
      return;
    }
    
    try {
      await api.post('/automated-campaigns/ab-tests', newABTest);
      toast.success('A/B test variant created');
      setShowCreateABTest(false);
      setNewABTest({ campaign_id: 1, variant_name: '', discount_percent: 15, description: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create A/B test');
    }
  };

  const viewABTestResults = async (campaignId) => {
    try {
      const res = await api.get(`/automated-campaigns/ab-tests/results?campaign_id=${campaignId}`);
      setAbTestResults(res.data);
    } catch (err) {
      toast.error('Failed to fetch results');
    }
  };

  // === RENDER ===

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automated-campaigns">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-heading text-foreground">Automated Campaigns</h2>
          <p className="text-muted-foreground">Birthday rewards, re-engagement, and A/B testing</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Scheduler Status */}
          {schedulerStatus && (
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm ${
              schedulerStatus.scheduler_running 
                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
            }`}>
              <div className={`w-2 h-2 rounded-full ${
                schedulerStatus.scheduler_running ? 'bg-green-500 animate-pulse' : 'bg-yellow-500'
              }`} />
              {schedulerStatus.scheduler_running ? 'Scheduler Running' : 'Scheduler Paused'}
            </div>
          )}
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Stats Overview */}
      <AutomatedStatsOverview stats={stats} />

      {/* Navigation Tabs */}
      <div className="flex gap-2 border-b border-border pb-2">
        {[
          { id: 'birthday', label: 'Birthday Rewards', icon: Cake },
          { id: 'reengagement', label: 'Re-engagement', icon: UserMinus },
          { id: 'abtesting', label: 'A/B Testing', icon: FlaskConical },
          { id: 'log', label: 'Activity Log', icon: Calendar }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              activeTab === tab.id 
                ? 'bg-primary text-primary-foreground' 
                : 'text-muted-foreground hover:bg-muted'
            }`}
            data-testid={`tab-${tab.id}`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Birthday Rewards Tab */}
      {activeTab === 'birthday' && (
        <BirthdayRewardsTab
          settings={birthdaySettings}
          upcomingBirthdays={upcomingBirthdays}
          sendingBirthday={sendingBirthday}
          onUpdateSettings={updateBirthdaySettings}
          onSendBirthdayReward={sendBirthdayReward}
          onRefresh={fetchData}
        />
      )}

      {/* Re-engagement Tab */}
      {activeTab === 'reengagement' && (
        <ReEngagementTab
          settings={reengagementSettings}
          eligibleContacts={eligibleContacts}
          sendingReengagement={sendingReengagement}
          onUpdateSettings={updateReengagementSettings}
          onSendReengagementOffer={sendReengagementOffer}
          onRefresh={fetchData}
        />
      )}

      {/* A/B Testing Tab */}
      {activeTab === 'abtesting' && (
        <ABTestingTab
          abTests={abTests}
          abTestResults={abTestResults}
          showCreateDialog={showCreateABTest}
          newABTest={newABTest}
          onSetShowCreateDialog={setShowCreateABTest}
          onSetNewABTest={setNewABTest}
          onCreateABTest={createABTest}
          onViewResults={viewABTestResults}
          onClearResults={() => setAbTestResults(null)}
        />
      )}

      {/* Activity Log Tab */}
      {activeTab === 'log' && (
        <ActivityLogTab campaignLog={campaignLog} />
      )}
    </div>
  );
}
